export const PCB_PANELS = ["24 X 18", "21 X 24", "16 X 14", "24 X 20", "10 X 12", "24 X 21", "18 X 24", "12 X 16", "16 X 12", "12 X 9", "14 X 16", "12 X 10", "9 X 12"];
export const PCB_BOARD_DEFAULTS = {
  "DC-SCM": { thickness: 1.5, layers: 10, clad: 4, copperOz: 8, drillHoles: 3000 },
  "E1.L": { thickness: 1.5, layers: 6, clad: 2, copperOz: 6, drillHoles: 2500 },
  "E1.S": { thickness: 1.5, layers: 6, clad: 2, copperOz: 6, drillHoles: 2500 },
  Fan: { thickness: 1, layers: 2, clad: 0, copperOz: 2, drillHoles: 20 },
  FPGA: { thickness: 1.6, layers: 18, clad: 8, copperOz: 12, drillHoles: 30000 },
  "HDD Backplane": { thickness: 2.5, layers: 16, clad: 7, copperOz: 20, drillHoles: 30000 },
  "M.2": { thickness: 1.6, layers: 4, clad: 1, copperOz: 4, drillHoles: 170 },
  "Mother Board": { thickness: 2.4, layers: 18, clad: 8, copperOz: 20, drillHoles: 20000 },
  OAM: { thickness: 4, layers: 24, clad: 11, copperOz: 30, drillHoles: 1000 },
  PCIe: { thickness: 1.6, layers: 6, clad: 2, copperOz: 6, drillHoles: 3500 },
  PDB: { thickness: 2, layers: 16, clad: 7, copperOz: 14, drillHoles: 30000 },
  Riser: { thickness: 1.6, layers: 6, clad: 2, copperOz: 6, drillHoles: 3500 },
  UBB: { thickness: 3, layers: 20, clad: 9, copperOz: 18, drillHoles: 25000 },
};
export const PCB_FALLBACK_DEFAULTS = { thickness: 1.6, layers: 18, clad: 8, copperOz: 20, drillHoles: 20000 };

// apiFetch rejects parsed API error bodies as well as native Error instances.
export function pcbCostErrorMessage(error, fallback = "PCB request failed. Please try again.") {
  const messages = [typeof error === "string" ? error : null, error?.error?.message, error?.error, error?.message];
  return messages.find(value => typeof value === "string" && value.trim())?.trim() || fallback;
}

function pcbQuoteError(message) {
  const error = new Error(`${message} No PCB price was generated.`);
  error.code = "PCB_COST_RESPONSE_INVALID";
  return error;
}

function pcbCostAmount(value) {
  if (typeof value !== "number" && typeof value !== "string") {
    throw pcbQuoteError("The pricing service returned missing or invalid cost amounts.");
  }
  const amount = pcbNumber(value);
  if (!Number.isFinite(amount) || amount < 0 || (amount === 0 && typeof value === "string" && /[1-9]/.test(value.split(/[eE]/)[0]))) {
    throw pcbQuoteError("The pricing service returned missing or invalid cost amounts.");
  }
  return amount;
}

// Validate the service contract without changing its amounts. The backend
// combines consumables and indirect materials into the packaging bucket.
export function validatedPcbCostResponse(response) {
  const source = response?.breakdown;
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    throw pcbQuoteError("The pricing service did not return a complete cost breakdown.");
  }
  const fields = ["material", "process", "packaging", "ohp", "total"];
  const amounts = Object.fromEntries(fields.map(field => [field, pcbCostAmount(source[field])]));
  if (!(amounts.material > 0 && amounts.total > 0)) throw pcbQuoteError("The pricing service returned nonpositive PCB material or total cost.");
  for (const [key, value] of Object.entries(source)) {
    if (fields.includes(key) || key === "currency" || key === "total_should_cost") continue;
    // Unknown metadata remains compatible. A separately named nonzero cost
    // would have an ambiguous allocation and must not become another row.
    if (["consumables", "indirect", "indirect_material", "logistics", "overhead", "profit", "freight"].includes(key)
        || /(?:cost|charge|fee|tax)(?:s)?$/i.test(key)) {
      if (pcbCostAmount(value) !== 0) throw pcbQuoteError("The pricing service returned ambiguous additional cost buckets.");
    }
  }
  const subtotal = fields.slice(0, 4).reduce((sum, field) => sum + amounts[field], 0);
  // Four buckets and their total may each be rounded to four decimals.
  const tolerance = 5 * 0.00005 + Number.EPSILON * Math.max(1, subtotal, amounts.total) * 8;
  if (!Number.isFinite(subtotal) || Math.abs(subtotal - amounts.total) > tolerance
      || (Object.hasOwn(source, "total_should_cost") && Math.abs(pcbCostAmount(source.total_should_cost) - amounts.total) > tolerance)) {
    throw pcbQuoteError("The pricing service returned costs that do not reconcile with the total.");
  }
  return { ...response, breakdown: { ...amounts, ...(source.currency ? { currency: source.currency } : {}) } };
}

export function validatedPcbStreamCost(event) {
  const quote = event?.ShouldCostResult;
  if (!quote || typeof quote !== "object" || Array.isArray(quote)) {
    throw pcbQuoteError("The pricing stream did not return a complete quote.");
  }
  for (const field of ["ConsumablesCost", "IndirectMaterialCost", "LogisticsCost"]) {
    if (Object.hasOwn(quote, field) && pcbCostAmount(quote[field]) !== 0) {
      throw pcbQuoteError("The pricing stream returned ambiguous additional cost buckets.");
    }
  }
  return validatedPcbCostResponse({ breakdown: { material: quote.MaterialCost, process: quote.ProcessCost,
    packaging: quote.PackagingCost, ohp: quote.OHPCost, total: quote.TotalCost } });
}

// The API validates its SQL layout against this same requested panel model.
// Check the returned priced layout before publishing money or PlayBook state;
// never replace a malformed response with freshly calculated display values.
export function validatedPcbEffectiveLayout(layout, product, playbook) {
  const invalid = () => pcbQuoteError("The pricing service did not return a valid effective panel layout.");
  if (!layout || typeof layout !== "object" || Array.isArray(layout)) throw invalid();
  const expected = pcbPanelLayout(playbook?.panelSizeIn, product?.boardLengthMm, product?.boardWidthMm);
  if (!expected || expected.boardsPerPanel < 1 || layout.panelSizeIn !== expected.panelSizeIn) throw invalid();
  const numeric = {};
  for (const field of ["boardsPerPanel", "utilizationPct", "orientationDegrees", "panelAreaMm2"]) {
    if (typeof layout[field] !== "number" && typeof layout[field] !== "string") throw invalid();
    numeric[field] = pcbNumber(layout[field]);
    if (!Number.isFinite(numeric[field]) || (numeric[field] === 0 && typeof layout[field] === "string" && /[1-9]/.test(layout[field].split(/[eE]/)[0]))) throw invalid();
  }
  if (!Number.isSafeInteger(numeric.boardsPerPanel) || numeric.boardsPerPanel < 1
      || numeric.utilizationPct <= 0 || numeric.utilizationPct > 100
      || ![0, 90].includes(numeric.orientationDegrees) || numeric.panelAreaMm2 <= 0) throw invalid();
  if (numeric.boardsPerPanel !== expected.boardsPerPanel || numeric.orientationDegrees !== expected.orientationDegrees) throw invalid();
  for (const field of ["utilizationPct", "panelAreaMm2"]) {
    if (Math.abs(numeric[field] - expected[field]) > Math.max(0.000001, Math.abs(expected[field]) * 0.000000001)) throw invalid();
  }
  return { ...layout, ...numeric };
}

export function pcbNumber(value) {
  if (typeof value === "boolean" || value === null || value === undefined || String(value).trim() === "") return NaN;
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(String(value).trim())) return NaN;
  const number = Number(value);
  return Number.isFinite(number) ? number : NaN;
}

export function pcbPanelLayout(panel, length, width) {
  const l = pcbNumber(length), w = pcbNumber(width);
  if (!(l > 0 && w > 0)) return null;
  const canonical = String(panel || "").trim().replace(/\s*[xX*]\s*/, " X ");
  if (!PCB_PANELS.includes(canonical)) return null;
  const [a, b] = canonical.split(" X ").map(Number);
  const panelLengthMm = a * 25.4, panelWidthMm = b * 25.4;
  const fit = (x, y) => x > panelLengthMm - 18 + 1e-9 || y > panelWidthMm - 18 + 1e-9 ? 0 : Math.floor((panelLengthMm - 18 + 2 + 1e-9) / (x + 2)) * Math.floor((panelWidthMm - 18 + 2 + 1e-9) / (y + 2));
  const normal = fit(l, w), rotated = fit(w, l), boardsPerPanel = Math.max(normal, rotated);
  const panelAreaMm2 = panelLengthMm * panelWidthMm;
  return { panelSizeIn: canonical, boardsPerPanel, orientationDegrees: rotated > normal ? 90 : 0, utilizationPct: boardsPerPanel * l * w / panelAreaMm2 * 100, panelAreaMm2 };
}

export function bestPcbPanel(product = {}) {
  let best = null;
  for (const panel of PCB_PANELS) {
    const layout = pcbPanelLayout(panel, product.boardLengthMm, product.boardWidthMm);
    if (!layout || layout.boardsPerPanel < 1) continue;
    if (!best || layout.utilizationPct > best.utilizationPct || (Math.abs(layout.utilizationPct - best.utilizationPct) < 0.0001 && layout.boardsPerPanel > best.boardsPerPanel)) best = layout;
  }
  return best;
}

export function isPcbProductValid(product) {
  if (!product || typeof product.productName !== "string" || !product.productName.trim() || product.productName.trim().length > 50 || !product.boardType || !String(product.manufacturingCountry || "").trim()) return false;
  const l = pcbNumber(product.boardLengthMm), w = pcbNumber(product.boardWidthMm), batch = pcbNumber(product.estimatedBatchSize);
  return l >= 5 && l <= 610 && w >= 5 && w <= 610 && Number.isInteger(batch) && batch >= 1 && batch <= 10000 && Boolean(bestPcbPanel(product));
}

export function buildPcbRunPlayBook(product, previous = {}) {
  const best = PCB_BOARD_DEFAULTS[product.boardType] || PCB_FALLBACK_DEFAULTS;
  const bestPanel = bestPcbPanel(product);
  if (!bestPanel) throw new Error("This board does not fit a supported panel with 9 mm margins and 2 mm spacing.");
  const values = { materialTg: "155", boardFinish: "ENIG", materialType: "Mid loss", boardThicknessMm: product.boardThicknessMm || best.thickness,
    panelSizeIn: bestPanel.panelSizeIn, layerCount: best.layers, cladLaminateCount: best.clad, cuAlThicknessOz: best.copperOz, drilledHoleCount: best.drillHoles,
    blindHoleCount: 0, buriedHoleCount: 0, goldFingerCount: 0, edgePlating: false, unbalancedStack: false, impedanceControl: false };
  const overrideFields = { ...(previous?.overrideFields || {}) };
  for (const field of Object.keys(values)) if (overrideFields[field] === true && Object.prototype.hasOwnProperty.call(previous, field)) values[field] = previous[field];
  const selected = pcbPanelLayout(values.panelSizeIn, product.boardLengthMm, product.boardWidthMm);
  if (!selected || selected.boardsPerPanel < 1) throw new Error("This board does not fit the selected panel. Choose Best in Class or a larger panel.");
  return { ...values, overrideFields, boardType: product.boardType, boardLengthMm: product.boardLengthMm, boardWidthMm: product.boardWidthMm,
    boardsPerPanel: selected.boardsPerPanel, utilization: selected.utilizationPct, orientationDegrees: selected.orientationDegrees, overhead: null };
}
